import {USER_PREMIUM_LEVELS} from '@Utils/user.util';
import type {AppliedPromoCode, Plan, PlanFeature, PromoEligiblePlans} from '@Panels/premium-pricing-panel/premium-pricing-panel.types';
import {PlanCycle, PROMO_DISCOUNT_TYPE_PERCENTAGE, PROMO_MONTHS_FOREVER, PROMO_MONTHS_ONE_TIME, PROMO_MONTHS_SINGLE_CYCLE, PKR_CURRENCY_CODE, PREMIUM_MONTHLY_CREDITS, PREMIUM_MONTHLY_CREDITS_PKR, PREMIUM_PLUS_MONTHLY_CREDITS, PREMIUM_PLUS_MONTHLY_CREDITS_PKR, TRIAL_DOWNLOADS_INCLUDED} from '@Panels/premium-pricing-panel/premium-pricing-panel.types';
import {isMonthly} from '@Libraries/change-subscription-plan-library';
import type {SubscriptionPlan} from '@Panels/change-plan-modal/change-plan-modal.types';
import {formatNumberInCurrency} from '@Utils/number.util';
import {escapeHTML} from '@Utils/string.util';
import {PMW_FEATURE_CONSTANTS} from './premium-pricing-panel.constants';

function getPlanDescription(planLevel: USER_PREMIUM_LEVELS): string {
  if (planLevel === USER_PREMIUM_LEVELS.PREMIUM) {
    return window.i18next.t('pmwjs_get_more_powerful_design_tools_and_start_marketing_your_brand_like_a_pro');
  } else if (planLevel === USER_PREMIUM_LEVELS.PREMIUM_PLUS) {
    return window.i18next.t('pmwjs_all_about_growth_take_your_marketing_and_branding_to_new_heights');
  }

  return '';
}

function getMonthlyCredits(planLevel: USER_PREMIUM_LEVELS, currencyCode: string): number {
  const isPremiumPlus = planLevel === USER_PREMIUM_LEVELS.PREMIUM_PLUS;
  if (currencyCode === PKR_CURRENCY_CODE) {
    return isPremiumPlus ? PREMIUM_PLUS_MONTHLY_CREDITS_PKR : PREMIUM_MONTHLY_CREDITS_PKR;
  }
  return isPremiumPlus ? PREMIUM_PLUS_MONTHLY_CREDITS : PREMIUM_MONTHLY_CREDITS;
}

function getPlanFeatures(planLevel: USER_PREMIUM_LEVELS, currencyCode: string): PlanFeature[] {
  if (planLevel === USER_PREMIUM_LEVELS.PREMIUM) {
    const features: PlanFeature[] = [
      {
        featureName: 'everything_free',
        icon: 'icon-check',
        description: window.i18next.t('pmwjs_everything_included_in_free'),
      },
      {
        featureName: PMW_FEATURE_CONSTANTS.AUTO_CAPTION,
        icon: 'icon-sparkle',
        description: window.i18next.t('pmwjs_create_and_edit_with_ai_prompts'),
      },
      {
        featureName: PMW_FEATURE_CONSTANTS.POST_FREE_DOWNLOAD,
        icon: 'icon-cloud-download',
        description: window.i18next.t('pmwjs_unlimited_downloads_no_watermark'),
      },
      {
        featureName: PMW_FEATURE_CONSTANTS.SCHEDULE_POST,
        icon: 'icon-clock-o',
        description: window.i18next.t('pmwjs_schedule_social_media_posts_and_emails'),
      },
      {
        featureName: PMW_FEATURE_CONSTANTS.TEXT_TO_IMAGE,
        icon: 'icon-ai-image',
        description: window.i18next.t('pmwjs_ai_images_ai_writer_ai_subtitles_and_more_ai_tools'),
      },
      {
        featureName: PMW_FEATURE_CONSTANTS.MORE_EVENTS_PREMIUM,
        icon: 'icon-event',
        description: window.i18next.t('pmwjs_attract_crowds_with_x_event_pages_per_month_and_shareable_calendar', {numEvents: 10}),
      },
      {
        featureName: PMW_FEATURE_CONSTANTS.TICKETING_LOWER_FEES,
        icon: 'icon-event',
        description: window.i18next.t('pmwjs_increased_ticket_cap'),
      },
      {
        featureName: PMW_FEATURE_CONSTANTS.ADD_SOCIAL_MEDIA_ACCOUNTS_PREMIUM,
        icon: 'icon-social-media',
        description: window.i18next.t('pmwjs_publish_on_x_social_media_accounts', {numAccounts: 3}),
      },
      {
        featureName: PMW_FEATURE_CONSTANTS.EMAIL_SENDS,
        icon: 'icon-envelope',
        description: window.i18next.t('pmwjs_send_x_trackable_emails_per_month', {numEmails: 3000}),
      },
      {
        featureName: PMW_FEATURE_CONSTANTS.BRANDS,
        icon: 'icon-brands',
        description: window.i18next.t('pmwjs_your_own_brand_kit_and_custom_fonts'),
      },
      {
        featureName: PMW_FEATURE_CONSTANTS.SPELL_CHECK,
        icon: 'icon-spellcheck',
        description: window.i18next.t('pmwjs_never_miss_a_typo_with_spellcheck'),
      },
    ];

    features.push({
      featureName: PMW_FEATURE_CONSTANTS.CREDITS,
      icon: 'icon-flash',
      description: window.i18next.t('pmwjs_x_new_credits_each_month', {numCredits: getMonthlyCredits(planLevel, currencyCode)}),
      tooltip: window.i18next.t('pmwjs_unused_credits_expire_at_end_of_month'),
    });
    return features;
  } else if (planLevel === USER_PREMIUM_LEVELS.PREMIUM_PLUS) {
    const features: PlanFeature[] = [
      {
        featureName: 'everything_premium',
        icon: 'icon-check',
        description: window.i18next.t('pmwjs_everything_included_in_premium'),
      },
      {
        featureName: PMW_FEATURE_CONSTANTS.MORE_EVENTS_PREMIUM_PLUS,
        icon: 'icon-event',
        description: window.i18next.t('pmwjs_unlimited_event_pages'),
      },
      {
        featureName: PMW_FEATURE_CONSTANTS.RECURRING_EVENTS,
        icon: 'icon-event',
        description: window.i18next.t('pmwjs_set_recurring_events'),
      },
      {
        featureName: PMW_FEATURE_CONSTANTS.TICKETING_LOWER_FEES,
        icon: 'icon-event',
        description: window.i18next.t('pmwjs_service_fees_drops_to_2_5_percent'),
      },
      {
        featureName: PMW_FEATURE_CONSTANTS.TICKETING_PLAN_ALLOWANCE,
        icon: 'icon-event',
        description: window.i18next.t('pmwjs_unlimited_ticket_sales'),
      },
      {
        featureName: PMW_FEATURE_CONSTANTS.AUTO_CAPTION,
        icon: 'icon-sparkle',
        description: window.i18next.t('pmwjs_more_credits_for_ai_prompts'),
      },
      {
        featureName: PMW_FEATURE_CONSTANTS.TEXT_TO_IMAGE,
        icon: 'icon-ai-image',
        description: window.i18next.t('pmwjs_more_custom_ai_images_highlighted'),
      },
      {
        featureName: PMW_FEATURE_CONSTANTS.BG_REMOVALS,
        icon: 'icon-remove-bg',
        description: window.i18next.t('pmwjs_more_ai_bg_removals_highlighted'),
      },
      {
        featureName: PMW_FEATURE_CONSTANTS.ADD_SOCIAL_MEDIA_ACCOUNTS_PREMIUM_PLUS,
        icon: 'icon-social-media',
        description: window.i18next.t('pmwjs_connect_unlimited_social_accounts'),
      },
      {
        featureName: PMW_FEATURE_CONSTANTS.EMAIL_SENDS,
        icon: 'icon-envelope',
        description: window.i18next.t('pmwjs_send_x_trackable_emails_per_month', {numEmails: 10000}),
      },
      {
        featureName: PMW_FEATURE_CONSTANTS.MORE_BRANDS,
        icon: 'icon-brands',
        description: window.i18next.t('pmwjs_unlimited_brand_kits'),
      },
      {
        featureName: PMW_FEATURE_CONSTANTS.CUSTOM_DOMAIN_ENABLED,
        icon: 'icon-at',
        description: window.i18next.t('pmwjs_branded_email_addresses'),
      },
    ];

    features.push({
      featureName: PMW_FEATURE_CONSTANTS.CREDITS,
      icon: 'icon-flash',
      description: window.i18next.t('pmwjs_x_new_credits_each_month', {numCredits: getMonthlyCredits(planLevel, currencyCode)}),
      tooltip: window.i18next.t('pmwjs_unused_credits_expire_at_end_of_month'),
    });
    return features;
  }

  return [];
}

const TRIAL_ALLOWANCE_FEATURE_NAMES = [PMW_FEATURE_CONSTANTS.CREDITS, PMW_FEATURE_CONSTANTS.POST_FREE_DOWNLOAD];

function isTrialAllowanceFeature(featureName: string): boolean {
  return TRIAL_ALLOWANCE_FEATURE_NAMES.includes(featureName);
}

/**
 * A trial grants half the plan's monthly credits, rounded down, matching `PremiumBillingFacade::addFirstTimeMetacurrenciesForTrial`.
 */
function getTrialCreditsFeatures(features: PlanFeature[], plan: Plan): PlanFeature[] {
  const creditsFeature = features.find((feature) => feature.featureName === PMW_FEATURE_CONSTANTS.CREDITS);
  if (!creditsFeature) {
    return [];
  }

  const monthlyCredits = getMonthlyCredits(plan.level, plan.currencyCode);
  return [
    {
      ...creditsFeature,
      description: window.i18next.t('pmwjs_x_credits_in_trial_y_credits_monthly_when_subscription_starts', {
        trialCredits: Math.floor(monthlyCredits / 2),
        monthlyCredits,
      }),
      centerAlign: true,
    },
  ];
}

function getTrialDownloadsFeature(): PlanFeature {
  return {
    featureName: PMW_FEATURE_CONSTANTS.POST_FREE_DOWNLOAD,
    icon: 'icon-cloud-download',
    description: window.i18next.t('pmwjs_x_downloads_in_trial_unlimited_when_subscription_starts', {numDownloads: TRIAL_DOWNLOADS_INCLUDED}),
  };
}

/**
 * A trial grants less than the plan does once billing starts, so the trial card spells out both
 * allowances on every plan and lifts them to just below "Everything in ...", inside the lines mobile shows collapsed.
 */
export function transformFeaturesForTrial(features: PlanFeature[], plan: Plan): PlanFeature[] {
  const trialAllowanceFeatures = [...getTrialCreditsFeatures(features, plan), getTrialDownloadsFeature()];
  const remainingFeatures = features.filter((feature) => !isTrialAllowanceFeature(feature.featureName));

  const everythingIncludedIndex = remainingFeatures.findIndex((feature) => feature.featureName === 'everything_free' || feature.featureName === 'everything_premium');
  const insertIndex = everythingIncludedIndex === -1 ? 1 : everythingIncludedIndex + 1;
  return [...remainingFeatures.slice(0, insertIndex), ...trialAllowanceFeatures, ...remainingFeatures.slice(insertIndex)];
}

export function removeTrialDownloadsFeature(features: PlanFeature[]): PlanFeature[] {
  return features.filter((feature) => feature.featureName !== PMW_FEATURE_CONSTANTS.POST_FREE_DOWNLOAD);
}

export function getPlansToShow(plans: Plan[], selectedCycle: PlanCycle): Plan[] {
  return plans.filter((plan) => plan.cycle === selectedCycle).sort((a, b) => a.level - b.level);
}

const PREMIUM_PLUS_FEATURE_NAMES: ReadonlySet<string> = new Set([
  PMW_FEATURE_CONSTANTS.MORE_BRANDS,
  PMW_FEATURE_CONSTANTS.EDITOR_BRANDS,
  PMW_FEATURE_CONSTANTS.CUSTOM_DOMAIN_ENABLED,
  PMW_FEATURE_CONSTANTS.ADD_SOCIAL_MEDIA_ACCOUNTS_PREMIUM_PLUS,
  PMW_FEATURE_CONSTANTS.MORE_EVENTS_PREMIUM_PLUS,
  PMW_FEATURE_CONSTANTS.EVENTS_REMOVE_WATERMARK,
  PMW_FEATURE_CONSTANTS.RECURRING_EVENTS,
  // Premium Plus is the tier with no ticket/sales allowance at all, so it is the card to outline when an
  // organiser arrives from the plan-allowance banner.
  PMW_FEATURE_CONSTANTS.TICKETING_PLAN_ALLOWANCE,
]);

export function getHighlightedLevelForFeature(featureName?: string): USER_PREMIUM_LEVELS {
  if (featureName !== undefined && featureName !== '' && PREMIUM_PLUS_FEATURE_NAMES.has(featureName)) {
    return USER_PREMIUM_LEVELS.PREMIUM_PLUS;
  }
  return USER_PREMIUM_LEVELS.PREMIUM;
}

export function mapSubscriptionPlanToPlan(subscriptionPlan: SubscriptionPlan): Plan {
  return {
    priceId: subscriptionPlan.price.id,
    name: subscriptionPlan.level === USER_PREMIUM_LEVELS.PREMIUM.valueOf() ? window.i18next.t('pmwjs_premium') : window.i18next.t('pmwjs_premium_plus'),
    description: getPlanDescription(subscriptionPlan.level),
    price: subscriptionPlan.tiers[0].displayPrice,
    currencyCode: subscriptionPlan.price.currencyCode,
    level: subscriptionPlan.level,
    features: getPlanFeatures(subscriptionPlan.level, subscriptionPlan.price.currencyCode),
    cycle: isMonthly(subscriptionPlan.duration) ? PlanCycle.MONTHLY : PlanCycle.YEARLY,
  };
}

export function getPremiumMonthlyPrice(plans: Plan[]): number {
  return getMonthlyPlanPrice(plans, true);
}

export function getPremiumPlusMonthlyPrice(plans: Plan[]): number {
  return getMonthlyPlanPrice(plans, false);
}

function getMonthlyPlanPrice(plans: Plan[], isPremium: boolean): number {
  const level = isPremium ? USER_PREMIUM_LEVELS.PREMIUM : USER_PREMIUM_LEVELS.PREMIUM_PLUS;
  for (const plan of plans) {
    if (plan.level === level && plan.cycle === PlanCycle.MONTHLY) {
      return plan.price;
    }
  }

  return 0;
}

export function getPremiumYearlyPrice(plans: Plan[]): number {
  for (const plan of plans) {
    if (plan.level === USER_PREMIUM_LEVELS.PREMIUM && plan.cycle === PlanCycle.YEARLY) {
      return plan.price;
    }
  }

  return 0;
}

export function getYearlyDiscountPercentage(plans: Plan[]): number {
  const yearlyPrice = getPremiumYearlyPrice(plans);
  const annualisedMonthlyPrice = getPremiumMonthlyPrice(plans) * PlanCycle.YEARLY;
  if (annualisedMonthlyPrice <= 0 || yearlyPrice <= 0) {
    return 0;
  }

  return Math.round((1 - yearlyPrice / annualisedMonthlyPrice) * 100);
}

function isNumberArray(value: unknown): value is number[] {
  return Array.isArray(value) && value.every((entry) => typeof entry === 'number');
}

function isPromoEligiblePlans(value: unknown): value is PromoEligiblePlans {
  if (typeof value !== 'object' || value === null || !('levels' in value) || !('months' in value)) {
    return false;
  }

  return isNumberArray(value.levels) && isNumberArray(value.months);
}

/**
 * `premium/getPromoCodeDetails` answers for any live promotion, including flat-amount ones whose
 * `discount` is a currency value rather than a fraction. Anything that is not a usable percentage is
 * rejected here, so nothing downstream has to re-check it.
 */
export function toAppliedPromoCode(code: string, response: unknown): AppliedPromoCode | null {
  if (typeof response !== 'object' || response === null) {
    return null;
  }

  if (!('discount' in response) || !('discountType' in response) || !('months' in response) || !('plans' in response)) {
    return null;
  }

  const {discount, discountType, months, plans} = response;
  if (discountType !== PROMO_DISCOUNT_TYPE_PERCENTAGE) {
    return null;
  }

  if (typeof discount !== 'number' || discount <= 0 || discount > 1) {
    return null;
  }

  if (typeof months !== 'number' || !isPromoEligiblePlans(plans)) {
    return null;
  }

  return {
    code: code,
    discountFraction: discount,
    months: months,
    eligiblePlans: plans,
  };
}

/**
 * The single gate for everything a promotion changes on a plan card, so its banner, its price and its
 * billing copy cannot disagree with one another.
 */
export function isPromoApplicableToPlan(appliedPromoCode: AppliedPromoCode | null, plan: Plan, selectedCycle: PlanCycle): boolean {
  if (appliedPromoCode === null) {
    return false;
  }

  return appliedPromoCode.eligiblePlans.levels.includes(plan.level) && isPromoApplicableToCycle(appliedPromoCode, selectedCycle);
}

/** The cycle half of the same gate: whether the promotion discounts the cycle the page is showing. */
export function isPromoApplicableToCycle(appliedPromoCode: AppliedPromoCode | null, cycle: PlanCycle): boolean {
  if (appliedPromoCode === null) {
    return false;
  }

  return appliedPromoCode.eligiblePlans.months.includes(cycle);
}

export function getDiscountedPlanPrice(plan: Plan, appliedPromoCode: AppliedPromoCode | null, selectedCycle: PlanCycle): number {
  if (appliedPromoCode === null || !isPromoApplicableToPlan(appliedPromoCode, plan, selectedCycle)) {
    return plan.price;
  }

  return plan.price * (1 - appliedPromoCode.discountFraction);
}

export function getPromoSavingsText(appliedPromoCode: AppliedPromoCode): string {
  return window.i18next.t('pmwjs_youre_saving_discount_with_code_code', {
    discount: Math.round(appliedPromoCode.discountFraction * 100),
    code: escapeHTML(appliedPromoCode.code),
  });
}

/**
 * How long the discounted price runs for, replacing the plain "/ month" or "/ year" on a covered card.
 * A promotion that never lapses keeps the plan's own cycle text, so it returns null.
 */
export function getPromoCycleText(appliedPromoCode: AppliedPromoCode, selectedCycle: PlanCycle): string | null {
  if (appliedPromoCode.months === PROMO_MONTHS_FOREVER) {
    return null;
  }

  const isYearlySelected = selectedCycle === PlanCycle.YEARLY;
  if (appliedPromoCode.months === PROMO_MONTHS_ONE_TIME || appliedPromoCode.months === PROMO_MONTHS_SINGLE_CYCLE) {
    return window.i18next.t(isYearlySelected ? 'pmwjs_for_one_year' : 'pmwjs_for_one_month');
  }

  return window.i18next.t('pmwjs_for_months_months', {months: appliedPromoCode.months});
}

export function getPromoFullPriceText(plan: Plan, selectedCycle: PlanCycle): string {
  const formattedFullPrice = formatNumberInCurrency(plan.price, plan.currencyCode);
  const textKey = selectedCycle === PlanCycle.YEARLY ? 'pmwjs_then_amount_per_year_after_that' : 'pmwjs_then_amount_per_month_after_that';

  return window.i18next.t(textKey, {amount: formattedFullPrice});
}
